import { AppNode } from "@features/builder/graph/types"
import { getParentsList } from "@utils/node/getParentsList"
import { InternalNode } from "@xyflow/react"

export type NodesMapper<TNode extends AppNode | InternalNode<AppNode> = AppNode> = {
  /** In edge relation ship parent nodeId to source node */
  sourceToTargetMap: Map<TNode["id"], TNode[]>
  /** In edge relation ship child nodeId to parent node */
  targetToSourceMap: Map<TNode["id"], TNode[]>
  /** Node Id to it's label mapping
   */
  idToLabelMap: Map<TNode["id"], string>
  /** Node label to it's id mapping
   */
  labelToIdMap: Map<TNode["id"], string>
  /** Node ID to node mapping */
  nodesMap: Map<TNode["id"], TNode>
}

/**
 * Builds edge-relationship and label lookup maps from all nodes: source↔target traversal,
 * id↔label lookups, and a full node map. `nodesMap` is populated from a `structuredClone` of the input.
 */
export function nodesMapper<TNode extends AppNode | InternalNode<AppNode>>(
  nodes: Iterable<TNode>,
): NodesMapper<TNode> {
  const freezedNodes = structuredClone([...nodes])

  const mapper: NodesMapper<TNode> = {
    sourceToTargetMap: new Map(),
    targetToSourceMap: new Map(),
    idToLabelMap: new Map(),
    labelToIdMap: new Map(),
    nodesMap: new Map<string, TNode>(freezedNodes.map((n) => [n.id, n])),
  }

  for (const node of freezedNodes) {
    if (node.data?.label) {
      mapper.idToLabelMap.set(node.id, node.data.label)
      mapper.labelToIdMap.set(node.data.label, node.id)
    }

    const parentIds = getParentsList(node)
    if (parentIds.length === 0) continue

    const parents: TNode[] = []

    for (const parentId of parentIds) {
      const parentNode = mapper.nodesMap.get(parentId)
      if (!parentNode) continue

      parents.push(parentNode)

      const children = mapper.sourceToTargetMap.get(parentId)
      if (children) {
        children.push(node)
      } else {
        mapper.sourceToTargetMap.set(parentId, [node])
      }
    }

    if (parents.length > 0) {
      mapper.targetToSourceMap.set(node.id, parents)
    }
  }

  return mapper
}
